/**
 * Sportconnect — Bootstrap JS du thème
 * Snazzy Digital Agency
 *
 * Chargé en module defer depuis layout/theme.liquid. Reste minimaliste :
 * - observation d'apparition pour les animations [data-animate]
 * - utilitaire global Sportconnect (event bus, helpers focus trap, etc.)
 *
 * Le JS spécifique de chaque section est embarqué dans la section elle-même,
 * uniquement chargé si la section est présente sur la page.
 */

const Sportconnect = window.Sportconnect || {};

Sportconnect.bus = Sportconnect.bus || new EventTarget();

Sportconnect.formatMoney = function (cents) {
  if (typeof Shopify === 'undefined' || !Shopify.formatMoney) return cents;
  return Shopify.formatMoney(cents, window.shop_money_format || '€{{amount}}');
};

/**
 * Observation des éléments [data-animate] pour appliquer .is-visible
 * quand ils entrent dans le viewport.
 */
function initScrollAnimations() {
  const motionPref = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (motionPref.matches) {
    document.querySelectorAll('[data-animate]').forEach((el) => el.classList.add('is-visible'));
    return;
  }
  if (!('IntersectionObserver' in window)) {
    document.querySelectorAll('[data-animate]').forEach((el) => el.classList.add('is-visible'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    },
    { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
  );

  document.querySelectorAll('[data-animate]').forEach((el) => observer.observe(el));
}

/**
 * Focus trap réutilisable (drawer, modale).
 */
Sportconnect.trapFocus = function (container, firstFocus) {
  const focusable = container.querySelectorAll(
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
  );
  if (focusable.length === 0) return () => {};

  const first = firstFocus || focusable[0];
  const last = focusable[focusable.length - 1];
  first.focus();

  function onKey(e) {
    if (e.key !== 'Tab') return;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
  container.addEventListener('keydown', onKey);
  return () => container.removeEventListener('keydown', onKey);
};

/**
 * Smooth-scroll sur les liens d'ancre internes (href="#xxx").
 * Active si <html data-smooth-anchor="true"> (driven by theme settings).
 * Tient compte de la hauteur du header sticky (.sc-header / .site-header)
 * pour ne pas que la cible soit recouverte. Respecte prefers-reduced-motion.
 * Ignore les clics avec modifier (cmd/ctrl/shift/alt) pour preserver
 * l'ouverture en nouvel onglet ou la copie de lien.
 */
function smoothAnchorClickHandler(e) {
  if (document.documentElement.dataset.smoothAnchor !== 'true') return;
  if (e.defaultPrevented) return;
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;

  const link = e.target.closest('a[href^="#"]');
  if (!link) return;
  const href = link.getAttribute('href');
  if (!href || href === '#' || href.length < 2) return;

  let target;
  try {
    target = document.querySelector(href);
  } catch (err) { /* selector invalide */ return; }
  if (!target) return;

  e.preventDefault();

  const header = document.querySelector('.sc-header, .site-header');
  const headerH = header ? header.getBoundingClientRect().height : 0;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const top = target.getBoundingClientRect().top + window.scrollY - headerH - 12;

  window.scrollTo({ top, behavior: reduce ? 'auto' : 'smooth' });

  // Focus la cible pour l'accessibilite (sans re-scroller)
  if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
  try { target.focus({ preventScroll: true }); } catch (err) { /* IE */ }

  // Met a jour l'URL sans declencher de scroll natif
  if (window.history && window.history.pushState) {
    window.history.pushState(null, '', href);
  }
}

function initSmoothAnchors() {
  document.removeEventListener('click', smoothAnchorClickHandler);
  document.addEventListener('click', smoothAnchorClickHandler);
}

document.addEventListener('DOMContentLoaded', () => {
  initScrollAnimations();
  initSmoothAnchors();
});

// Réinitialisation après chaque modification de l'éditeur de thème
document.addEventListener('shopify:section:load', () => {
  initScrollAnimations();
  initSmoothAnchors();
});

window.Sportconnect = Sportconnect;
